import { Injectable } from "@angular/core";
import { DbDaoBase } from "../../../../../gyzmo-commons/dao/db/base/db.dao.base";
import { NotImplementedError } from "../../../../../gyzmo-commons/helpers/notImplementedError";
import { AppSqlProvider } from "../../../../../gyzmo-commons/persistence/app.sql.provider";
import { LoggerService } from "../../../../../gyzmo-commons/services/logs/logger.service";
import { Zone } from "../../../../models/inspection/v2/zone.model";
import { DamageDbDaoV2 } from "./damage.db.dao";

@Injectable({
    providedIn: "root",
})
export class ZoneDbDaoV2 extends DbDaoBase<Zone> {
    constructor(
        logger: LoggerService,
        private sqlProvider: AppSqlProvider,
        private damageDbDao: DamageDbDaoV2,
    ) {
        super(logger);
    }

    getByFaceId(inspectionId: string, faceId: string, hydrate: boolean = false): Promise<Zone[]> {
        const selectQuery = `SELECT * FROM ${ Zone.TABLENAME } WHERE faceId = ?;`;
        return this.sqlProvider.query(selectQuery, [faceId])
            .then(data => {
                if (data.rows.length <= 0) {
                    return [];
                }
                const zones: Zone[] = [];
                for (const item of data.rows) {
                    const zone = this.rowToModel(item);
                    zones.push(zone);
                }
                const hydratationPromises = zones.map(zone =>
                    this.damageDbDao.getByInspectionIdAndZoneId(inspectionId, zone.id, hydrate)
                        .then(value => {
                            zone.damages = value;
                        }),
                );
                return Promise.all(hydratationPromises)
                    .then(() => zones);
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public async createIndexes(): Promise<void> {
        const query = `CREATE INDEX IF NOT EXISTS idx_${ Zone.TABLENAME }_faceId ON ${ Zone.TABLENAME }(faceId);`;
        await this.sqlProvider.query(query, [])
            .catch(reason => {
                this.logSqlError(reason);
            });
    }

    public createTable(): Promise<void> {
        const query = `
            CREATE TABLE IF NOT EXISTS ${ Zone.TABLENAME } (
                id TEXT PRIMARY KEY,
                faceId TEXT,
                shapeType TEXT,
                coordinates TEXT,
                zoneClick TEXT
            );`;
        return this.sqlProvider.query(query, [])
            .then(async () => {
                await this.createIndexes();
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    delete(id: string): Promise<any> {
        const deleteQuery = `DELETE FROM ${ Zone.TABLENAME } WHERE id = ?;`;
        return this.sqlProvider.query(deleteQuery, [id]);
    }

    deleteAll(): Promise<any> {
        const deleteAllQuery = `DELETE FROM ${ Zone.TABLENAME };`;
        return this.sqlProvider.query(deleteAllQuery, []);
    }

    public get(id: string, hydrate: boolean = false): Promise<Zone> {
        throw new NotImplementedError();
    }

    public getTableName(): string {
        return Zone.TABLENAME;
    }

    protected rowToModel(row: any): Zone {
        const zone = new Zone();
        zone.id = row.id;
        zone.faceId = row.faceId;
        zone.shapeType = row.shapeType;
        zone.coordinates = row.coordinates;
        zone.zoneClick = row.zoneClick;
        return zone;
    }

    public save(zone: Zone): Promise<Zone> {
        const promises = zone.damages.map(damage => this.damageDbDao.save(damage));
        return Promise.all(promises).then(() => {
            const query = `
                INSERT OR REPLACE INTO ${ Zone.TABLENAME }
                (id, faceId, shapeType, coordinates, zoneClick)
                VALUES (?, ?, ?, ?, ?);`;
            const params = [
                zone.id,
                zone.faceId,
                zone.shapeType,
                zone.coordinates,
                zone.zoneClick,
            ];
            return this.sqlProvider.query(query, params)
                .then(() => zone)
                .catch(reason => {
                    this.logSqlError(reason);
                    return null;
                });
        });
    }
}
